import dayjs from "dayjs";
import { reportPreviewService } from "./report-preview.service";
import { organizationService } from "@/features/organization/services";
import { getFrameworkMeta } from "@/features/reporting-boundary/constants/framework-metadata";

// Prefers the org's actual assigned-framework name (same source the
// Frameworks filter and exports resolve against) so this mock preview stays
// consistent with what gets exported; falls back to the static metadata
// table, then the raw code, for frameworks it doesn't recognize.
const resolveFrameworkName = (code, frameworkNameByCode) =>
  frameworkNameByCode?.get?.(code) || getFrameworkMeta(code).shortName || code;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatDateRange = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return "-";
  return `${start.format("MMM YYYY")} - ${end.format("MMM YYYY")}`;
};

const DIMENSION_LABEL_MAP = {
  level_1: "BU",
  level_2: "Company",
  level_3: "Site",
  country: "Country",
};

const DETAIL_DIMENSION_FIELD_MAP = {
  level_1: "bu",
  level_2: "companyAncestor",
  level_3: "owner",
  country: "country",
};

const parseValueUnit = (value, unit) => {
  if (unit) {
    const v =
      value && value.endsWith(` ${unit}`)
        ? value.slice(0, -(unit.length + 1)) || "-"
        : value || "-";
    return [v, unit];
  }
  if (!value) return ["-", "-"];
  const lastSpace = value.lastIndexOf(" ");
  if (lastSpace !== -1) {
    const left = value.slice(0, lastSpace);
    const right = value.slice(lastSpace + 1);
    if (!isNaN(parseFloat(left.replace(/,/g, "")))) return [left, right];
  }
  return [value, "-"];
};

const buildConsolidatedTableHtml = (rows = [], buSide, t) => {
  const showDimension = Boolean(buSide) && buSide !== "parent_company";
  const dimensionLabel = DIMENSION_LABEL_MAP[buSide] || "Dimension";
  const gridCols = showDimension
    ? "grid-cols-[0.7fr_0.6fr_1.2fr_1fr_0.7fr_0.9fr_0.8fr_0.5fr]"
    : "grid-cols-[0.7fr_0.6fr_1.4fr_0.7fr_0.9fr_0.8fr_0.5fr]";
  return `
  <div class="overflow-hidden rounded-radius border border-slate-200 bg-white">
    <div class="grid ${gridCols} gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] uppercase tracking-[0.18em] text-slate-500">
      <span>${escapeHtml(t("Code"))}</span>
      <span>${escapeHtml(t("Cross-Ref"))}</span>
      <span>${escapeHtml(t("Title"))}</span>
      ${showDimension ? `<span>${escapeHtml(t(dimensionLabel))}</span>` : ""}
      <span>${escapeHtml(t("Reporting Year"))}</span>
      <span>${escapeHtml(t("ESG Category"))}</span>
      <span>${escapeHtml(t("Result"))}</span>
      <span>${escapeHtml(t("Unit"))}</span>
    </div>
    <div class="divide-y divide-slate-200 text-sm text-slate-600">
      ${rows
        .map((item) => {
          const [vd, ud] =
            !item.value && item.remark
              ? ["n.a", "-"]
              : parseValueUnit(item.value, item.unit);
          const crossRef =
            Array.isArray(item.crossRefCodes) && item.crossRefCodes.length
              ? item.crossRefCodes.join(", ")
              : "-";
          return `
            <div class="grid ${gridCols} gap-3 px-4 py-3">
              <span class="font-medium text-slate-900">${escapeHtml(item.code)}</span>
              <span class="text-slate-500">${escapeHtml(crossRef)}</span>
              <span>${escapeHtml(item.codeTitle || "-")}</span>
              ${showDimension ? `<span>${escapeHtml(item.dimensionName || "-")}</span>` : ""}
              <span>${escapeHtml(item.yearLabel || item.year || "-")}</span>
              <span>${escapeHtml(item.pillar || "-")}</span>
              <span class="text-slate-900">${escapeHtml(vd)}</span>
              <span class="text-slate-500">${escapeHtml(ud)}</span>
            </div>
          `;
        })
        .join("")}
    </div>
  </div>
`;
};

const buildHierarchyHtml = (item) => {
  const segments =
    Array.isArray(item.ancestorPath) && item.ancestorPath.length
      ? item.ancestorPath
      : [item.parentLevel, item.owner].filter(Boolean);
  if (!segments.length) return "-";
  return segments
    .map((s, i) => {
      const isLast = i === segments.length - 1;
      const style = isLast ? "color:#0f172a;font-weight:500" : "color:#64748b";
      return `<span style="${style}">${escapeHtml(s)}</span>`;
    })
    .join(' <span style="color:#cbd5e1">&gt;</span> ');
};

const buildDetailTableHtml = (rows = [], buSide, t) => {
  const showDimension = Boolean(buSide) && buSide !== "parent_company";
  const dimensionLabel = DIMENSION_LABEL_MAP[buSide] || "Dimension";
  const dimensionField = DETAIL_DIMENSION_FIELD_MAP[buSide];
  const gridCols = showDimension
    ? "grid-cols-[0.7fr_0.6fr_1fr_1fr_1.4fr_0.9fr_0.7fr_0.8fr_0.8fr_0.5fr]"
    : "grid-cols-[0.7fr_0.6fr_1.2fr_2fr_0.9fr_0.7fr_0.8fr_0.8fr_0.5fr]";
  return `
  <div class="overflow-hidden rounded-radius border border-slate-200 bg-white">
    <div class="grid ${gridCols} gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] uppercase tracking-[0.18em] text-slate-500">
      <span>${escapeHtml(t("Code"))}</span>
      <span>${escapeHtml(t("Cross-Ref"))}</span>
      <span>${escapeHtml(t("Title"))}</span>
      ${showDimension ? `<span>${escapeHtml(t(dimensionLabel))}</span>` : ""}
      <span>${escapeHtml(t("Organization"))}</span>
      <span>${escapeHtml(t("ESG Category"))}</span>
      <span>${escapeHtml(t("Reporting Year"))}</span>
      <span>${escapeHtml(t("Date"))}</span>
      <span>${escapeHtml(t("Result"))}</span>
      <span>${escapeHtml(t("Unit"))}</span>
    </div>
    <div class="divide-y divide-slate-200 text-sm text-slate-600">
      ${rows
        .map((item) => {
          const [vd, ud] =
            !item.value && item.remark
              ? ["n.a", "-"]
              : parseValueUnit(item.value, item.unit);
          const crossRef =
            Array.isArray(item.crossRefCodes) && item.crossRefCodes.length
              ? item.crossRefCodes.join(", ")
              : "-";
          return `
            <div class="grid ${gridCols} gap-3 px-4 py-3">
              <span class="font-medium text-slate-900">${escapeHtml(item.code)}</span>
              <span class="text-slate-500">${escapeHtml(crossRef)}</span>
              <span>${escapeHtml(item.codeTitle || "-")}</span>
              ${showDimension ? `<span>${escapeHtml(item[dimensionField] || "-")}</span>` : ""}
              <span style="display:flex;flex-wrap:wrap;align-items:center;gap:4px;font-size:11px;">${buildHierarchyHtml(item)}</span>
              <span>${escapeHtml(item.pillar || "-")}</span>
              <span>${escapeHtml(item.yearLabel || item.year || "-")}</span>
              <span>${escapeHtml(item.date || "-")}</span>
              <span class="text-slate-900">${escapeHtml(vd)}</span>
              <span class="text-slate-500">${escapeHtml(ud)}</span>
            </div>
          `;
        })
        .join("")}
    </div>
  </div>
`;
};

const getReportFilters = (report) => ({
  buSide: report.buSide,
  entityIds: Array.isArray(report.companies) ? report.companies : [],
  includeAllEntities: Boolean(report.includeAllEntities),
  consolidation: Boolean(report.consolidation),
  startDate: report.startDate,
  endDate: report.endDate,
});

const getOrganizationText = (report) =>
  (report?.entityLabels?.length
    ? report.entityLabels
    : report?.companies
  )?.join(", ") || "-";

const resolveOrganizationPayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

export const loadPreviewData = async (report) => {
  if (!report) return null;
  if (report?.id) {
    const resolved = await reportPreviewService.fetchReport(report.id);
    return {
      filtersApplied: resolved?.filtersApplied || null,
      preview: resolved?.preview || null,
      report: resolved?.report || report,
      source: resolved?.source || "live",
    };
  }
  return reportPreviewService.fetchPreview({
    filters: getReportFilters(report),
  });
};

export const buildPreviewContent = ({ report, preview, filterLabel, t }) => {
  const rows = Array.isArray(preview?.preview?.table?.rows)
    ? preview.preview.table.rows
    : [];
  const consolidated = Boolean(preview?.preview?.table?.consolidated);

  return {
    title: report?.name || t("Preview"),
    description: t("Review the current report content before exporting."),
    badge: consolidated ? t("Consolidated") : t("Detailed"),
    cover: {
      eyebrow: t("ESG Reporting"),
      title: report?.name || t("Preview"),
      description:
        report?.description ||
        t("A document-style preview of the generated reporting output."),
      meta: [
        {
          label: t("Date range"),
          value: formatDateRange(report?.startDate, report?.endDate),
        },
        { label: t("Filter"), value: filterLabel || "-" },
      ],
    },
    navigationItems: [
      { id: "organizations", label: t("Organizations") },
      { id: "preview-data", label: t("Preview Data") },
    ],
    sections: [
      {
        id: "organizations",
        title: t("Organizations"),
        html: `<p class="text-sm leading-6 text-slate-700">${escapeHtml(
          getOrganizationText(report),
        )}</p>`,
      },
      {
        id: "preview-data",
        title: t("Preview Data"),
        description: `${rows.length} ${t("entries")}`,
        html: consolidated
          ? buildConsolidatedTableHtml(rows, report?.buSide, t)
          : buildDetailTableHtml(rows, report?.buSide, t),
      },
    ],
  };
};

export const buildOrgNodeMaps = (tree = []) => {
  const nodeById = new Map();
  const companyToSites = new Map();
  const buToCompanies = new Map();
  const companyToBu = new Map();
  const countryToCompanies = new Map();
  const siteToCompany = new Map();

  const pushToMap = (map, key, value) => {
    if (!key || !value) return;
    const list = map.get(key) || [];
    if (!list.includes(value)) list.push(value);
    map.set(key, list);
  };

  const resolveCountry = (node) =>
    node?.locationLabel ||
    node?.countryLabel ||
    node?.country ||
    node?.regionLabel;

  const walk = (node, currentBu, currentCompany) => {
    const nodeId = node?.id ?? node?.name;
    if (!nodeId) return;
    const normalizedId = String(nodeId);
    nodeById.set(normalizedId, node);
    const nextBu = node?.unitLevel === "level_1" ? normalizedId : currentBu;
    const nextCompany =
      node?.unitLevel === "level_2" ? normalizedId : currentCompany;

    if (node?.unitLevel === "level_2") {
      if (!companyToSites.has(normalizedId)) {
        companyToSites.set(normalizedId, []);
      }
      if (nextBu) pushToMap(buToCompanies, nextBu, normalizedId);
      if (nextBu) companyToBu.set(normalizedId, nextBu);
      const countryLabel = resolveCountry(node);
      if (countryLabel) {
        pushToMap(countryToCompanies, String(countryLabel), normalizedId);
      }
    }

    if (node?.unitLevel === "level_3" && nextCompany) {
      pushToMap(companyToSites, nextCompany, normalizedId);
      siteToCompany.set(normalizedId, nextCompany);
    }

    (node?.children || []).forEach((child) => walk(child, nextBu, nextCompany));
  };

  (tree || []).forEach((node) => walk(node, null, null));

  return {
    nodeById,
    companyToSites,
    buToCompanies,
    companyToBu,
    countryToCompanies,
    siteToCompany,
  };
};

const PREVIEW_SVG = {
  building: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>`,
  landmark: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="21" y1="22" y2="22"/><line x1="6" x2="6" y1="18" y2="11"/><line x1="10" x2="10" y1="18" y2="11"/><line x1="14" x2="14" y1="18" y2="11"/><line x1="18" x2="18" y1="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>`,
  git: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="6" y1="3" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>`,
  globe: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.54 15H17a2 2 0 0 0-2 2v4.54"/><path d="M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17"/><path d="M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05"/><circle cx="12" cy="12" r="10"/></svg>`,
};

export const getEntityLevelMeta = (level) => {
  const map = {
    parent_company: { svg: PREVIEW_SVG.building, color: "#4f46e5" },
    level_1: { svg: PREVIEW_SVG.building, color: "#2563eb" },
    level_2: { svg: PREVIEW_SVG.landmark, color: "#059669" },
    level_3: { svg: PREVIEW_SVG.git, color: "#d97706" },
    country: { svg: PREVIEW_SVG.globe, color: "#0f766e" },
  };
  return map[level] || { svg: PREVIEW_SVG.building, color: "#64748b" };
};

export const buildSelectedRelationshipTree = ({
  selected = [],
  buSide,
  includeAllEntities,
  maps,
  t,
}) => {
  const {
    nodeById,
    companyToSites,
    buToCompanies,
    companyToBu,
    countryToCompanies,
    siteToCompany,
  } = maps;

  const resolveLabel = (id) => nodeById.get(String(id))?.name || String(id);

  if (!selected.length) return [];

  if (buSide === "parent_company") {
    const buildNode = (node, includeChildren = true) => ({
      id: String(node?.id || ""),
      title: node?.name || "",
      level: node?.unitLevel || "parent_company",
      children:
        includeChildren && Array.isArray(node?.children)
          ? node.children.map((child) => buildNode(child, true))
          : [],
    });

    return selected
      .map((parentId) => nodeById.get(String(parentId)))
      .filter(Boolean)
      .map((node) => buildNode(node, Boolean(includeAllEntities)));
  }

  if (buSide === "level_1") {
    return selected.map((buId) => ({
      id: String(buId),
      title: resolveLabel(buId),
      level: "level_1",
      children: includeAllEntities
        ? (buToCompanies.get(String(buId)) || []).map((companyId) => ({
            id: String(companyId),
            title: resolveLabel(companyId),
            level: "level_2",
            children: (companyToSites.get(String(companyId)) || []).map(
              (siteId) => ({
                id: String(siteId),
                title: resolveLabel(siteId),
                level: "level_3",
                children: [],
              }),
            ),
          }))
        : [],
    }));
  }

  if (buSide === "level_2") {
    const grouped = new Map();
    selected.forEach((companyId) => {
      const buId = companyToBu.get(String(companyId)) || "ungrouped";
      const list = grouped.get(buId) || [];
      list.push(companyId);
      grouped.set(buId, list);
    });
    return Array.from(grouped.entries()).map(([buId, companies]) => ({
      id: String(buId),
      title: buId === "ungrouped" ? t("Organization") : resolveLabel(buId),
      level: "level_1",
      children: companies.map((companyId) => ({
        id: String(companyId),
        title: resolveLabel(companyId),
        level: "level_2",
        children: includeAllEntities
          ? (companyToSites.get(String(companyId)) || []).map((siteId) => ({
              id: String(siteId),
              title: resolveLabel(siteId),
              level: "level_3",
              children: [],
            }))
          : [],
      })),
    }));
  }

  if (buSide === "level_3") {
    const grouped = new Map();
    selected.forEach((siteId) => {
      const companyId = siteToCompany.get(String(siteId)) || "ungrouped";
      const list = grouped.get(companyId) || [];
      list.push(siteId);
      grouped.set(companyId, list);
    });
    return Array.from(grouped.entries()).map(([companyId, sites]) => ({
      id: String(companyId),
      title:
        companyId === "ungrouped" ? t("Organization") : resolveLabel(companyId),
      level: "level_2",
      children: sites.map((siteId) => ({
        id: String(siteId),
        title: resolveLabel(siteId),
        level: "level_3",
        children: [],
      })),
    }));
  }

  if (buSide === "country") {
    return selected.map((countryId) => ({
      id: String(countryId),
      title: String(countryId).replace(/^country:/, ""),
      level: "country",
      children: (
        countryToCompanies.get(String(countryId).replace(/^country:/, "")) || []
      ).map((companyId) => ({
        id: String(companyId),
        title: resolveLabel(companyId),
        level: "level_2",
        children: includeAllEntities
          ? (companyToSites.get(String(companyId)) || []).map((siteId) => ({
              id: String(siteId),
              title: resolveLabel(siteId),
              level: "level_3",
              children: [],
            }))
          : [],
      })),
    }));
  }

  return [];
};

export const renderStructureNodes = (nodes = [], depth = 0) =>
  nodes
    .map((node, index) => {
      const meta = getEntityLevelMeta(node.level);
      const isLast = index === nodes.length - 1;
      return `
        <div style="position: relative; padding-left: ${depth === 0 ? 0 : 24}px;">
          ${
            depth > 0
              ? `<div style="position:absolute; left:${depth === 1 ? 6 : 30}px; top:0; bottom:${isLast ? "50%" : "0"}; width:1px; background:#e2e8f0;"></div>
                 <div style="position:absolute; left:${depth === 1 ? 6 : 30}px; top:20px; width:16px; height:1px; background:#e2e8f0;"></div>`
              : ""
          }
          <div style="display:flex; align-items:flex-start; gap:10px; padding:8px 0;">
            <span style="display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px; flex-shrink:0; border-radius:9999px; background:#fff; border:1px solid #e2e8f0; color:${meta.color};">
              ${meta.svg}
            </span>
            <div style="min-width:0; flex:1;">
              <p style="margin:0; font-size:13px; line-height:1.45; color:#0f172a; font-weight:${depth === 0 ? 600 : 500};">${escapeHtml(node.title)}</p>
            </div>
          </div>
          ${node.children?.length ? renderStructureNodes(node.children, depth + 1) : ""}
        </div>
      `;
    })
    .join("");

export const loadMockPreviewData = async (report) => {
  const [hierarchyResult, previewResult] = await Promise.allSettled([
    organizationService.fetchHierarchy({ throwOnError: false }),
    report?.id
      ? reportPreviewService.fetchReport(report.id, { throwOnError: false })
      : reportPreviewService.fetchPreview(
          {
            filters: {
              buSide: report?.buSide,
              entityIds: Array.isArray(report?.companies)
                ? report.companies
                : [],
              includeAllEntities: Boolean(report?.includeAllEntities),
              consolidation: Boolean(report?.consolidation),
              startDate: report?.startDate,
              endDate: report?.endDate,
            },
          },
          { throwOnError: false },
        ),
  ]);

  let organizationTree = [];
  if (hierarchyResult.status === "fulfilled") {
    const payload = resolveOrganizationPayload(hierarchyResult.value) || {};
    organizationTree = Array.isArray(payload.tree) ? payload.tree : [];
  }

  return {
    id: report?.id || null,
    generatedAt: dayjs().toISOString(),
    organizationTree,
    preview:
      previewResult.status === "fulfilled"
        ? {
            filtersApplied: previewResult.value?.filtersApplied || null,
            preview: previewResult.value?.preview || null,
            report: previewResult.value?.report || report,
            source: previewResult.value?.source || "live",
          }
        : null,
  };
};

export const buildMockPreviewContent = ({
  report,
  data,
  t,
  frameworkNameByCode,
}) => {
  const reportingPeriod = formatDateRange(report?.startDate, report?.endDate);
  const preparedBy =
    report?.preparedBy ||
    report?.createdByName ||
    report?.updatedByName ||
    "NXStart ESG Team";
  const orgMaps = buildOrgNodeMaps(data?.organizationTree || []);
  const organizationStructure = buildSelectedRelationshipTree({
    selected: report?.companies || [],
    buSide: report?.buSide,
    includeAllEntities: Boolean(report?.includeAllEntities),
    maps: orgMaps,
    t,
  });
  const allRows = Array.isArray(data?.preview?.preview?.table?.rows)
    ? data.preview.preview.table.rows
    : [];
  const consolidated = Boolean(data?.preview?.preview?.table?.consolidated);
  const PILLAR_ORDER = ["General", "Environmental", "Social", "Governance"];
  const dynamicSections = Array.from(
    new Set(allRows.map((item) => item.pillar).filter(Boolean)),
  )
    .sort((a, b) => {
      const ai = PILLAR_ORDER.indexOf(a);
      const bi = PILLAR_ORDER.indexOf(b);
      return (
        (ai === -1 ? PILLAR_ORDER.length : ai) -
        (bi === -1 ? PILLAR_ORDER.length : bi)
      );
    })
    .map((pillar) => ({
      id: `data-${pillar.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      label: `${pillar} ${t("Data")}`,
      rows: allRows.filter((item) => item.pillar === pillar),
    }));
  const tableOfContentsItems = [
    { id: "about-report", label: t("About Report") },
    { id: "company-location-data", label: t("Company Location Data") },
    { id: "framework", label: t("Framework") },
    ...dynamicSections,
  ];
  const sectionNumberMap = Object.fromEntries(
    tableOfContentsItems.map((item, index) => [item.id, index + 1]),
  );
  const sectionTitle = (id, label) => `${sectionNumberMap[id]}. ${label}`;

  // Collect IDs of nodes included in the report (from the filtered tree)
  const selectedLocationIds = new Set();
  const collectLocationIds = (nodes) => {
    for (const node of nodes) {
      if (node.id != null) selectedLocationIds.add(String(node.id));
      if (node.children?.length) collectLocationIds(node.children);
    }
  };
  collectLocationIds(organizationStructure);

  // Walk the full organizationTree (parent company at root) so ancestorPath
  // always contains the complete hierarchy — same as Hierarchy column in preview
  const flattenStructure = (nodes = []) => {
    const result = [];
    const walk = (list, ancestorPath = []) =>
      list.forEach((n) => {
        const id = String(n.id ?? n.name ?? "");
        if (selectedLocationIds.has(id)) {
          result.push({
            ...n,
            title: n.name || n.title || "-",
            ancestorPath: [...ancestorPath],
          });
        }
        if (n.children?.length)
          walk(n.children, [...ancestorPath, n.name || n.title || "-"]);
      });
    walk(nodes);
    return result;
  };
  const allLocationNodes = flattenStructure(data?.organizationTree || []);
  const companyLocationRows =
    allLocationNodes
      .map((structNode) => {
        const node = orgMaps.nodeById.get(String(structNode.id));
        const name = structNode.title;
        const meta = getEntityLevelMeta(structNode.level);
        const region = node?.regionLabel || node?.region || "-";
        const country =
          node?.countryLabel || node?.country || node?.locationLabel || "-";
        const address = node?.address || node?.addressLabel || "-";
        const upperLevel = structNode.ancestorPath?.length
          ? structNode.ancestorPath.join(" > ")
          : "-";
        return `
        <div class="grid grid-cols-[2fr_1.5fr_1.2fr_1.2fr_1.5fr] gap-3 px-4 py-3">
          <div style="display:flex; align-items:center; gap:10px;">
            <span style="display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px; flex-shrink:0; border-radius:9999px; background:#fff; border:1px solid #e2e8f0; color:${meta.color};">
              ${meta.svg}
            </span>
            <span class="font-medium text-slate-900">${escapeHtml(name)}</span>
          </div>
          <span class="text-slate-500 text-xs">${escapeHtml(upperLevel)}</span>
          <span>${escapeHtml(region)}</span>
          <span>${escapeHtml(country)}</span>
          <span>${escapeHtml(address)}</span>
        </div>
      `;
      })
      .join("") ||
    `
      <div class="px-4 py-3 text-sm text-slate-500">${escapeHtml(t("No location data available."))}</div>
    `;
  const coverHtml = `
    <div class="relative overflow-hidden">
      <img
        src="/images/report_cover.png"
        alt="${escapeHtml(t("Report cover"))}"
        class="absolute inset-0 h-full w-full object-cover"
      />
      <div class="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-slate-900/10"></div>
      <div class="relative min-h-[780px] px-10 py-12 md:px-16 md:py-14 text-slate-900">
        <div class="flex h-full min-h-[780px] flex-col justify-between">

          <!-- Top bar: logo right-aligned -->
          <div class="flex items-center justify-end">
            <img
              src="/images/nxstart_logo.png"
              alt="NXStart"
              class="h-16 object-contain drop-shadow-[0_1px_4px_rgba(255,255,255,0.5)]"
            />
          </div>

          <!-- Center: year + report type -->
          <div class="w-fit rounded-radius border border-white/40 bg-white/40 px-5 py-4 backdrop-blur-sm">
            <div class="text-7xl font-bold leading-none text-slate-950 md:text-8xl">${escapeHtml(
              dayjs().format("YYYY"),
            )}</div>
            <p class="mt-2 text-xs font-semibold uppercase tracking-[0.38em] text-slate-700">${escapeHtml(
              t("Sustainability Report"),
            )}</p>
          </div>

          <!-- Bottom block -->
          <div class="space-y-8">

            <!-- Accent bar + title block -->
            <div class="flex justify-end">
              <div class="max-w-[600px] border-r-4 border-slate-700/40 pr-6 text-right">
                <p class="text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-600">${escapeHtml(
                  t("ESG Reporting"),
                )}</p>
                <h1 class="mt-2 text-4xl font-semibold leading-tight text-slate-950 drop-shadow-[0_2px_12px_rgba(255,255,255,0.5)] md:text-5xl">
                  ${escapeHtml(report?.name || t("ESG Report Preview"))}
                </h1>
                <p class="mt-3 text-sm text-slate-700">
                  ${escapeHtml(t("Reporting Period"))}:
                  <span class="font-semibold text-slate-900"> ${escapeHtml(reportingPeriod)}</span>
                </p>
              </div>
            </div>

            <!-- Footer meta row -->
            <div class="grid grid-cols-3 gap-6 border-t border-slate-400/40 pt-5">
              <div>
                <p class="text-[10px] uppercase tracking-[0.24em] text-slate-600">${escapeHtml(t("Prepared By"))}</p>
                <p class="mt-1.5 text-sm font-semibold text-slate-950">${escapeHtml(preparedBy)}</p>
              </div>
              <div>
                <p class="text-[10px] uppercase tracking-[0.24em] text-slate-600">${escapeHtml(t("Presented To"))}</p>
                <p class="mt-1.5 text-sm font-semibold text-slate-950">${escapeHtml(report?.presentedTo || "JJ-Group")}</p>
              </div>
              <div class="text-right">
                <p class="text-[10px] uppercase tracking-[0.24em] text-slate-600">${escapeHtml(t("Generated"))}</p>
                <p class="mt-1.5 text-sm font-semibold text-slate-950">${escapeHtml(dayjs().format("DD MMM YYYY"))}</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  `;

  const sections = [
    {
      id: "table-of-contents",
      title: t("Table of Contents"),
      html: `
          <div class="rounded-radius border border-slate-200 bg-white p-8">
            <ol class="space-y-4">
              ${tableOfContentsItems
                .map(
                  (item, index) => `
                    <li class="border-b border-dashed border-slate-300 pb-3 text-sm">
                      <a href="#${escapeHtml(item.id)}" class="font-medium text-slate-900">${index + 1}. ${escapeHtml(item.label)}</a>
                    </li>
                  `,
                )
                .join("")}
            </ol>
          </div>
        `,
    },
    {
      id: "about-report",
      title: sectionTitle("about-report", t("About Report")),
      html: `
          <div class="space-y-4 text-sm leading-7 text-slate-700">
            <p style="text-indent: 2em;">${escapeHtml(report?.description || t("This ESG Report presents the organization's commitment to sustainability, responsible operations, and long-term value creation. It outlines key achievements and progress across Environmental, Social, and Governance priorities. The report reflects a strong focus on transparency, accountability, and delivering positive impact for stakeholders and the communities served."))}</p>
          </div>
        `,
    },
    {
      id: "company-location-data",
      title: sectionTitle("company-location-data", t("Company Location Data")),
      html: `
          <div class="overflow-hidden rounded-radius border border-slate-200 bg-white">
            <div class="grid grid-cols-[2fr_1.5fr_1.2fr_1.2fr_1.5fr] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] uppercase tracking-[0.18em] text-slate-500">
              <span>${escapeHtml(t("Entity"))}</span>
              <span>${escapeHtml(t("Upper-level"))}</span>
              <span>${escapeHtml(t("Region"))}</span>
              <span>${escapeHtml(t("Country"))}</span>
              <span>${escapeHtml(t("Address"))}</span>
            </div>
            <div class="divide-y divide-slate-100 text-sm text-slate-700">
              ${companyLocationRows}
            </div>
          </div>
        `,
    },
    {
      id: "framework",
      title: sectionTitle("framework", t("Framework")),
      description: t(
        "ESG reporting frameworks and standards applied in this report.",
      ),
      html: (() => {
        const selectedCodes = Array.isArray(report?.frameworks)
          ? report.frameworks
          : Array.isArray(report?.frameworkCodes)
            ? report.frameworkCodes
            : [];
        if (selectedCodes.length === 0) {
          return `<p class="px-4 py-3 text-sm text-slate-500">${escapeHtml(t("All frameworks included — no framework filter applied."))}</p>`;
        }
        return `
            <div class="overflow-hidden rounded-radius border border-slate-200 bg-white">
              <div class="grid grid-cols-[1fr_2fr] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                <span>${escapeHtml(t("Framework"))}</span>
                <span>${escapeHtml(t("Description"))}</span>
              </div>
              <div class="divide-y divide-slate-100 text-sm text-slate-700">
                ${selectedCodes
                  .map((code) => {
                    const meta = getFrameworkMeta(code);
                    return `
                      <div class="grid grid-cols-[1fr_2fr] gap-3 px-4 py-3">
                        <span class="font-semibold text-slate-900">${escapeHtml(resolveFrameworkName(code, frameworkNameByCode))}</span>
                        <span>${escapeHtml(meta.description || "-")}</span>
                      </div>
                    `;
                  })
                  .join("")}
              </div>
            </div>
          `;
      })(),
    },
    ...dynamicSections.map((group) => ({
      id: group.id,
      title: sectionTitle(group.id, group.label),
      html: group.rows.length
        ? consolidated
          ? buildConsolidatedTableHtml(group.rows, report?.buSide, t)
          : buildDetailTableHtml(group.rows, report?.buSide, t)
        : `<p class="text-sm italic text-slate-400">${escapeHtml(t("No data available."))}</p>`,
    })),
  ];

  return {
    title: report?.name || t("Preview"),
    description: t("Review the current report content before exporting."),
    badge: consolidated ? t("Consolidated") : t("Detailed"),
    coverHtml: report?.includeCoverPage === false ? null : coverHtml,
    showNavigation: false,
    sections:
      report?.includeContentPage === false
        ? sections.filter((section) => section.id !== "table-of-contents")
        : sections,
  };
};

export default loadPreviewData;
